import { CurrencyPipe } from '@angular/common';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  TemplateRef,
  untracked,
  ViewContainerRef,
  viewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  PatientMedicationResponseItem,
  PatientSupplementResponseItem,
  PatientSymptom,
  QuickViewActivityItem,
  SymptomStatus,
} from '@models';
import { DateParserService } from '@services';
import {
  ButtonComponent,
  SkeletonComponent,
  SymptomStatusLabels,
  ZonedDatePipe,
} from '@ui-lib';
import {
  activityIconOf,
  activityTimeFormat,
} from './patient-quick-view-activity';
import {
  formatBalanceLabel,
  hasBalanceDue,
} from './patient-quick-view-billing';
import { PatientQuickViewCardComponent } from './patient-quick-view-card.component';
import { formatIntake, medicationName } from './patient-quick-view-intake';
import { formatNoteDate } from './patient-quick-view-identity';
import { PatientQuickViewStore } from './patient-quick-view.store';
import {
  displayNameOf,
  formatDateOfBirthLine,
  initialsOf,
  NOT_AVAILABLE,
  orNotAvailable,
} from './patient-quick-view-identity';
import { formatLastVisit, formatNextVisit } from './patient-quick-view-visits';
import { MinorIndicatorComponent } from '../../../shared/components/minor-indicator/minor-indicator.component';
import {
  guardianContactState,
  RETAINED_GUARDIAN_NOTE,
} from '../../../shared/guardian-contact/guardian-contact-state';

const DRAWER_WIDTH_PX = '580px';

@Component({
  selector: 'provider-portal-patient-quick-view',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    MatTooltipModule,
    SkeletonComponent,
    ButtonComponent,
    PatientQuickViewCardComponent,
    ZonedDatePipe,
    MinorIndicatorComponent,
  ],
  providers: [CurrencyPipe],
  templateUrl: './patient-quick-view.component.html',
})
export class PatientQuickViewComponent {
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly store = inject(PatientQuickViewStore);
  private readonly currency = inject(CurrencyPipe);
  private readonly dateParser = inject(DateParserService);

  private readonly panel = viewChild.required<TemplateRef<unknown>>('panel');

  private overlayRef: OverlayRef | null = null;

  protected readonly notAvailable = NOT_AVAILABLE;

  protected activityIcon(item: QuickViewActivityItem): string {
    return activityIconOf(item.type);
  }

  protected activityTimeFormat(item: QuickViewActivityItem): string {
    const zone = this.store.displayTimeZone();
    const occurred = this.dateParser.toZonedWallClock(item.occurredAtUtc, zone);
    const now = this.dateParser.toZonedWallClock(new Date(), zone);

    return activityTimeFormat(
      occurred !== null &&
        now !== null &&
        occurred.getFullYear() === now.getFullYear() &&
        occurred.getMonth() === now.getMonth() &&
        occurred.getDate() === now.getDate()
    );
  }

  protected readonly hasBalanceDue = computed(() =>
    hasBalanceDue(this.store.billing())
  );

  protected readonly balanceAriaLabel = computed(
    () => `${this.balanceLabel()}. View invoices for ${this.displayName()}`
  );

  protected readonly balanceLabel = computed(() =>
    formatBalanceLabel(
      this.store.billing(),
      dollars => this.currency.transform(dollars, 'USD') ?? ''
    )
  );

  protected readonly displayName = computed(() => {
    const patient = this.store.patient();

    return patient ? displayNameOf(patient) : '';
  });

  protected readonly initials = computed(() => {
    const patient = this.store.patient();

    return patient ? initialsOf(patient) : '';
  });

  protected readonly dateOfBirthLine = computed(() =>
    formatDateOfBirthLine(this.store.patient()?.dateOfBirth)
  );

  protected readonly phone = computed(() =>
    orNotAvailable(this.store.patient()?.phoneNumber)
  );

  protected readonly email = computed(() =>
    orNotAvailable(this.store.patient()?.email)
  );

  protected readonly isMinor = computed(
    () => this.store.patient()?.isMinor === true
  );

  protected readonly guardianState = computed(() => {
    const patient = this.store.patient();

    return patient
      ? guardianContactState(patient.guardianContact, patient.isMinor)
      : 'hidden';
  });

  protected readonly guardian = computed(
    () => this.store.patient()?.guardianContact ?? null
  );

  protected readonly retainedGuardianNote = RETAINED_GUARDIAN_NOTE;

  protected readonly lastVisitLabel = computed(() =>
    formatLastVisit(this.store.lastVisit(), this.store.displayTimeZone())
  );

  protected readonly nextVisitLabel = computed(() =>
    formatNextVisit(this.store.nextVisit(), this.store.displayTimeZone())
  );

  protected readonly hasNextVisit = computed(
    () => this.store.nextVisit() !== null
  );

  protected readonly openVisitHint = computed(() =>
    this.hasNextVisit() ? '' : 'No upcoming visit'
  );

  protected readonly canOpenVisit = computed(
    () => this.store.ready() && this.hasNextVisit()
  );

  protected readonly visitErrorMessage = computed(() => {
    switch (this.store.visitStartError()) {
      case 'stale':
        return 'This visit is no longer available';
      case 'failed':
        return 'Could not open the visit';
      default:
        return null;
    }
  });

  protected readonly canRetryVisit = computed(
    () => this.store.visitStartError() === 'failed'
  );

  protected readonly summary = computed(() => this.store.summary());

  protected readonly summaryText = computed(() => {
    const summary = this.summary();

    return summary.kind === 'ready' ? summary.text : '';
  });

  protected readonly summaryCaption = computed(() => {
    const summary = this.summary();

    if (summary.kind !== 'ready') {
      return '';
    }

    const status = summary.isSigned ? 'Signed' : 'Draft';

    return `AI-generated from the note of ${formatNoteDate(summary.noteDate)} (${status})`;
  });

  protected medicationName(medication: PatientMedicationResponseItem): string {
    return medicationName(medication);
  }

  protected intakeLabel(
    row: PatientMedicationResponseItem | PatientSupplementResponseItem
  ): string {
    return formatIntake(row);
  }

  protected statusLabel(symptom: PatientSymptom): string {
    return SymptomStatusLabels[symptom.status];
  }

  protected statusClass(symptom: PatientSymptom): string {
    switch (symptom.status) {
      case SymptomStatus.Worsening:
        return 'text-red-600';
      case SymptomStatus.Improving:
        return 'text-green-600';
      default:
        return 'text-gray-500';
    }
  }

  protected statusDotClass(symptom: PatientSymptom): string {
    switch (symptom.status) {
      case SymptomStatus.Worsening:
        return 'bg-red-500';
      case SymptomStatus.Improving:
        return 'bg-green-500';
      default:
        return 'bg-gray-400';
    }
  }

  constructor() {
    effect(() => {
      if (this.store.isOpen()) {
        untracked(() => this.attach());
        return;
      }

      untracked(() => this.detach());
    });

    this.destroyRef.onDestroy(() => this.detach());
  }

  private attach(): void {
    if (this.overlayRef) {
      return;
    }

    this.overlayRef = this.overlay.create({
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      positionStrategy: this.overlay.position().global().right('0').top('0'),
      width: DRAWER_WIDTH_PX,
      height: '100%',
      panelClass: 'provider-portal-quick-view-panel',
    });

    this.overlayRef
      .keydownEvents()
      .subscribe(event => event.key === 'Escape' && this.store.close());

    this.overlayRef.attach(
      new TemplatePortal(this.panel(), this.viewContainerRef)
    );
  }

  private detach(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }
}
